import { For, Show, type Component } from 'solid-js';

import { isElite, type EntitySnapshot } from '../net/protocol';
import { isBlank, isChampion, plateVerdict } from './nameplate';
import type { GameStore } from '../state/store';
import { percent } from './ActionBar';

/**
 * What to call an entity.
 *
 * A monster's name is the **server's** — assembled from its archetype and any
 * elite prefix and sent finished, so the client never maps an id to English.
 * This used to invent "Husk 4" from the id; every mob in the game was a Husk
 * because that was the only word the client knew.
 *
 * Players have no catalogue entry and so no name on the wire. Deriving one from
 * their id is presentation of data the server *did* send, not invention.
 */
export function displayName(entity: EntitySnapshot): string {
  if (entity.kind === 'player') return entity.id.replace('player-', 'Wanderer ');
  if (entity.name) return entity.name;
  // A mob with no name means a snapshot from a server that predates this field.
  // Say so plainly rather than making something up.
  return 'Unknown';
}

/** An elite's trait tags, rendered the way the spec writes them: "(Extra Fast)". */
export function traitLabel(entity: EntitySnapshot): string {
  if (!entity.traits?.length) return '';
  return `(${entity.traits.join(', ')})`;
}

/**
 * Health bars and names floating over living mobs, pinned to the screen anchors the render loop
 * publishes each frame — and, since the visibility hierarchy, over **far fewer of them**.
 *
 * Every living mob used to carry a name *and* a bar at all times. Promoted from
 * `ideas/NAMEPLATE-HEALTHBAR-VISIBILITY-HERARCHY.md`, whose complaint was that fifteen to twenty
 * of those in a tight space is a wall of red lines and overlapping text hiding the one thing the
 * player is actually reading — where the monsters are standing.
 *
 * **The rules are in `nameplate.ts` and none of them are here.** This component asks
 * `plateVerdict` what to draw and draws it; the hierarchy is testable without a DOM, a camera or
 * a projection, which is what makes "a thirty-mob pack is completely clean" an assertion rather
 * than a screenshot.
 */
export const Nameplates: Component<{ store: GameStore }> = (props) => {
  // Deliberately depends on entities() alone, which changes at the server tick
  // rate. Folding anchors() in here would rebuild this array — and re-key the
  // <For> — on every rendered frame, which at 144 fps is a great deal of DOM work
  // for a list that has not actually changed. Each plate reads its own anchor
  // below instead, so a frame updates two style properties per plate and nothing
  // else.
  //
  // <For> still re-keys when the snapshot arrives, because entities() hands back
  // fresh objects — that is 30 times a second rather than 144. Moving to <Index>
  // would keep the DOM across snapshots too; not worth the added indirection until
  // there are enough entities on screen for it to show up in a profile.
  const plates = () => props.store.entities().filter((e) => e.kind === 'mob' && e.hp > 0);

  /**
   * How far a mob is from the player, in world units, or undefined when either is unknown.
   *
   * Off the snapshot rather than the anchors: an anchor is a screen position and says nothing
   * about depth, and the gate is about *how far away the fight is* rather than about where the
   * plate would land.
   */
  const distanceTo = (entity: EntitySnapshot): number | undefined => {
    const me = props.store.self();
    if (!me) return undefined;
    return Math.hypot(entity.x - me.x, entity.z - me.z);
  };

  return (
    <div class="nameplates" data-testid="nameplates">
      <For each={plates()}>
        {(entity) => {
          const anchor = () => props.store.anchors()[entity.id];
          /**
           * Recomputed per frame, because two of its inputs are per-frame: the clock that ages
           * the damage window, and the pointer.
           *
           * `frameNow()` is read for its reactivity as much as its value — without it a bar
           * would sit at full opacity until something unrelated re-rendered the plate, which is
           * exactly the push-versus-predict fault the cooldown sweeps had.
           */
          const verdict = () => {
            // Read for its reactivity, not its value: `damagedAtOf` is a plain map lookup, and
            // the snapshot that re-renders this row is applied *before* the damage that belongs
            // to it. See the signal in `store.ts`.
            props.store.damageEpoch();
            return plateVerdict(entity, {
              hovered: props.store.hoveredEntityId() === entity.id,
              targeted: props.store.targetId() === entity.id,
              damagedAt: props.store.damagedAtOf(entity.id),
              now: props.store.frameNow(),
              distance: distanceTo(entity),
            });
          };

          return (
            <Show when={anchor()?.visible && !isBlank(verdict())}>
              <div
                class="nameplate"
                classList={{
                  'nameplate--targeted': props.store.targetId() === entity.id,
                  'nameplate--elite': isElite(entity),
                  // Trash keeps a smaller, quieter bar even when it is showing one.
                  'nameplate--trash': !isChampion(entity),
                }}
                data-testid={`nameplate-${entity.id}`}
                data-showing={verdict().showName ? 'name+bar' : 'bar'}
                style={{
                  left: `${anchor()?.x ?? 0}px`,
                  top: `${anchor()?.y ?? 0}px`,
                  opacity: String(verdict().opacity),
                  '--plate-scale': String(verdict().scale),
                }}
              >
                <Show when={verdict().showName}>
                  <span class="nameplate__name">{displayName(entity)}</span>
                  <Show when={traitLabel(entity)}>
                    {(label) => <span class="nameplate__traits">{label()}</span>}
                  </Show>
                </Show>
                <Show when={verdict().showBar}>
                  <div class="nameplate__bar">
                    <div
                      class="nameplate__fill"
                      style={{ width: `${percent(entity.hp, entity.maxHp)}%` }}
                    />
                  </div>
                </Show>
              </div>
            </Show>
          );
        }}
      </For>
    </div>
  );
};

/**
 * The locked-on enemy, shown at the top of the screen the way an ARPG shows a
 * boss frame. Absent when nothing is targeted.
 */
export const TargetFrame: Component<{ store: GameStore }> = (props) => (
  <Show when={props.store.target()}>
    {(target) => (
      <div
        class="plate plate--riveted target-frame"
        classList={{ 'target-frame--elite': isElite(target()) }}
        data-testid="target-frame"
      >
        <div class="target-frame__name">
          {displayName(target())}
          <Show when={traitLabel(target())}>
            {(label) => <span class="target-frame__traits">{label()}</span>}
          </Show>
        </div>
        <div
          class="target-frame__bar"
          role="progressbar"
          aria-label={`${displayName(target())} health`}
          aria-valuenow={target().hp}
          aria-valuemin={0}
          aria-valuemax={target().maxHp}
        >
          <div
            class="target-frame__fill"
            style={{ width: `${percent(target().hp, target().maxHp)}%` }}
          />
        </div>
      </div>
    )}
  </Show>
);
